/**
 * Module 6E — /settings/audit-log
 *
 * Per-studio, append-only audit log read view.
 *
 * RBAC:
 *   • Owner    → full audit feed (all categories)
 *   • Accounts → finance + billing subset only (backend filters; we surface
 *                the scope returned by the API so the UI is honest about it)
 *   • Everyone else → never sees the sidebar entry (gated to audit_read_finance)
 *     and gets a 403 from the API if they hit the URL directly.
 *
 * Phase 7.0 — Nuptial Diaries Premium CRM design pass + compact-density
 * row sizing. VISUAL-ONLY (every data-testid, every axios call, every
 * useState/useEffect/useMemo, the 50-row pagination, the cursor logic,
 * and the 403 vs. generic toast branching is preserved bit-for-bit).
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronRight, Clock, ShieldAlert } from "lucide-react";
import Layout from "@/components/Layout";
import { api } from "../lib/api";

// ----------------------------------------------------------------------------
// Category chips for the filter row. Keep in sync with the dotted action enum
// in `services/audit.py:AUDIT_ACTIONS`.
// ----------------------------------------------------------------------------
const CATEGORY_CHIPS = [
  { key: "",         label: "All",         hint: "every category" },
  { key: "auth",     label: "Auth",        hint: "logins, lockouts, OAuth" },
  { key: "team",     label: "Team",        hint: "invites, role changes" },
  { key: "billing",  label: "Billing",     hint: "subscription activations / cancels" },
  { key: "finance",  label: "Finance",     hint: "payouts, expenses, transactions" },
  { key: "project",  label: "Projects",    hint: "status, signoffs, cancels" },
  { key: "record",   label: "Trash",       hint: "soft-delete, restore, purge" },
  { key: "catalog",  label: "Catalog",     hint: "service / event / category edits" },
  { key: "brand_kit",label: "Brand kit",   hint: "logo, color, typography" },
  { key: "audit",    label: "Audit reads", hint: "who viewed the audit log" },
];

// Phase 7.0 dot palette — red auth / blue project / green finance / purple
// brand_kit / amber team / indigo billing / violet catalog / muted record /
// slate audit / dark red admin / gold default.
const ACTION_DOT_COLOR = {
  auth:      "#A35C45",
  team:      "#B45309",
  billing:   "#4F46E5",
  finance:   "#3F6B51",
  project:   "#2563EB",
  record:    "#76705F",
  catalog:   "#7C3AED",
  brand_kit: "#9333EA",
  audit:     "#64748B",
  admin:     "#B91C1C",
};
const DEFAULT_DOT = "#B08D55";

const ROW_GRID = "110px 170px 200px 220px minmax(0,1fr) 120px";

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

function ActionBadge({ action }) {
  const root = (action || "").split(".")[0];
  const dot = ACTION_DOT_COLOR[root] || DEFAULT_DOT;
  return (
    <span
      data-testid={`audit-action-${action}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 11,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        color: "#2A2722",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: dot,
          flexShrink: 0,
        }}
      />
      {action}
    </span>
  );
}

function ActorCell({ row }) {
  const label = row.actor_label || "system";
  const isSystem = !row.actor_user_id;
  return (
    <div style={{ minWidth: 0, lineHeight: 1.2 }}>
      <div
        style={{
          fontSize: 12,
          color: "#22382C",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </div>
      {row.actor_role && (
        <div
          style={{
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "#A39A8B",
            marginTop: 1,
          }}
        >
          {row.actor_role}
        </div>
      )}
      {isSystem && (
        <div style={{ fontSize: 9, color: "#A39A8B", fontStyle: "italic", marginTop: 1 }}>
          system actor
        </div>
      )}
    </div>
  );
}

function TargetCell({ row }) {
  if (!row.target_id && !row.target_type) {
    return <span style={{ fontSize: 11, color: "#A39A8B" }}>—</span>;
  }
  return (
    <div style={{ minWidth: 0, lineHeight: 1.2 }}>
      <div
        title={row.target_id}
        style={{
          fontSize: 12,
          color: "#22382C",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {row.target_label || row.target_id}
      </div>
      {row.target_type && (
        <div
          style={{
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "#A39A8B",
            marginTop: 1,
          }}
        >
          {row.target_type}
        </div>
      )}
    </div>
  );
}

function MetadataPreview({ row }) {
  const items = [];
  if (row.diff && (row.diff.before || row.diff.after)) {
    const before = row.diff.before || {};
    const after  = row.diff.after  || {};
    const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)])).slice(0, 2);
    for (const k of keys) {
      const b = before[k];
      const a = after[k];
      const bStr = typeof b === "string" && b.startsWith("sha256:") ? "(hashed)" : String(b ?? "—");
      const aStr = typeof a === "string" && a.startsWith("sha256:") ? "(hashed)" : String(a ?? "—");
      items.push(
        <span
          key={k}
          style={{
            fontSize: 10.5,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            color: "#2A2722",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "block",
          }}
        >
          {k}: {bStr} → {aStr}
        </span>,
      );
    }
  }
  if (row.metadata) {
    const md = row.metadata;
    const interesting = ["amount", "role", "phase", "variant", "tier", "cadence",
      "reason", "total_purged", "chain_length", "sessions_revoked"];
    for (const k of interesting) {
      if (md[k] !== undefined && items.length < 4) {
        items.push(
          <span
            key={`md-${k}`}
            style={{
              fontSize: 10.5,
              color: "#76705F",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "block",
            }}
          >
            {k}:{" "}
            <span
              style={{
                color: "#22382C",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              }}
            >
              {String(md[k])}
            </span>
          </span>,
        );
      }
    }
  }
  if (row.ref) {
    items.push(
      <span
        key="ref"
        title={JSON.stringify(row.ref)}
        style={{
          fontSize: 9,
          fontStyle: "italic",
          color: "#A39A8B",
          display: "block",
        }}
      >
        ↳ ref {row.ref.collection}
      </span>,
    );
  }
  if (!items.length) return <span style={{ fontSize: 11, color: "#A39A8B" }}>—</span>;
  return <div style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>{items}</div>;
}

function IpCell({ row }) {
  if (!row.ip) {
    return (
      <span
        title="IP wiped after 180 days"
        style={{ fontSize: 10, fontStyle: "italic", color: "#A39A8B" }}
      >
        —
      </span>
    );
  }
  return (
    <span
      style={{
        fontSize: 11,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        color: "#76705F",
      }}
    >
      {row.ip}
    </span>
  );
}

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState(null);
  const [filter, setFilter] = useState({
    action_prefix: "",
    actor_user_id: "",
    target_type: "",
    from: "",
    to: "",
  });
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const fetchPage = async (append = false, useCursor = null) => {
    setLoading(true);
    try {
      const params = {
        limit: 50,
        ...(filter.action_prefix && { action_prefix: filter.action_prefix }),
        ...(filter.actor_user_id && { actor_user_id: filter.actor_user_id }),
        ...(filter.target_type   && { target_type:   filter.target_type }),
        ...(filter.from && { from: filter.from }),
        ...(filter.to   && { to:   filter.to }),
        ...(useCursor   && { cursor: useCursor }),
      };
      const { data } = await api.get("/audit-log", { params });
      const items = data.items || [];
      setRows(append ? [...rows, ...items] : items);
      setScope(data.scope);
      setCursor(data.next_cursor);
      setHasMore(!!data.next_cursor);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 403) {
        toast.error("You don't have access to the audit log.");
      } else {
        toast.error("Failed to load audit log.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage(false, null);
  }, [filter.action_prefix, filter.actor_user_id, filter.target_type, filter.from, filter.to]);

  const scopeBadge = useMemo(() => {
    if (scope === "accounts_finance_subset") {
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "4px 10px",
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.10em",
            textTransform: "uppercase",
            background: "#E4EDDF",
            color: "#3F6B51",
            border: "1px solid #C7D8C0",
            borderRadius: 6,
          }}
        >
          Finance subset
        </span>
      );
    }
    if (scope === "owner_full") {
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "4px 10px",
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.10em",
            textTransform: "uppercase",
            background: "#F2EBD9",
            color: "#9A7A3C",
            border: "1px solid #E4DCCD",
            borderRadius: 6,
          }}
        >
          Full audit
        </span>
      );
    }
    return null;
  }, [scope]);

  return (
    <Layout>
      <div className="fade-up" data-testid="audit-log-page">
        {/* Page header */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
            marginBottom: 22,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "#B08D55",
                marginBottom: 8,
              }}
            >
              AUDIT
            </div>
            <h1
              className="font-display audit-h1"
              style={{ fontSize: 44, lineHeight: 1.05, color: "#22382C", margin: 0 }}
            >
              Audit Log
            </h1>
            <p style={{ fontSize: 13, color: "#76705F", margin: "8px 0 0", maxWidth: 720, lineHeight: 1.5 }}>
              Append-only record of accountability-relevant actions — who did what, when.
              Sensitive values are hashed; IPs wipe after 180 days.
            </p>
            {/* Phase 7.1 Pass 5 — mobile-only layout tweaks. Zero JSX/logic
                changes. Turns the 6-col audit row into a stacked card,
                horizontally scrolls the filter chip strip, stacks the
                secondary 4-col filter grid into a single column. */}
            <style>{`
              @media (max-width: 1023px) {
                .audit-h1 { font-size: 34px !important; font-weight: 500 !important; }
                [data-testid="audit-filter-chips"] {
                  flex-wrap: nowrap !important;
                  overflow-x: auto !important;
                  margin: 0 -16px 14px !important;
                  padding: 2px 16px 8px !important;
                  scrollbar-width: none;
                }
                [data-testid="audit-filter-chips"]::-webkit-scrollbar { display: none; }
                [data-testid="audit-filter-chips"] > button {
                  flex-shrink: 0;
                  white-space: nowrap;
                  min-height: 36px !important;
                  padding: 8px 14px !important;
                }
                .audit-secondary-filters {
                  grid-template-columns: 1fr 1fr !important;
                  gap: 8px !important;
                }
                .audit-secondary-filters .al-input { min-height: 44px; }
                /* Hide the desktop column-header row on mobile */
                .audit-col-header { display: none !important; }
                /* Convert audit rows to stacked cards */
                [data-testid^="audit-row-"] {
                  display: flex !important;
                  flex-direction: column !important;
                  align-items: stretch !important;
                  gap: 8px !important;
                  padding: 14px 15px !important;
                  border-radius: 14px !important;
                  border-color: rgba(34,56,44,0.10) !important;
                  background: #FFFDFA !important;
                  box-shadow: none !important;
                }
                [data-testid^="audit-row-"] > * { min-width: 0; }
              }
            `}</style>
          </div>
          <div>{scopeBadge}</div>
        </div>

        {/* Category filter chips */}
        <div
          data-testid="audit-filter-chips"
          style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}
        >
          {CATEGORY_CHIPS.map((c) => {
            const active = filter.action_prefix === c.key;
            return (
              <button
                key={c.key || "all"}
                onClick={() => setFilter((f) => ({ ...f, action_prefix: c.key }))}
                title={c.hint}
                data-testid={`audit-filter-${c.key || "all"}`}
                style={{
                  padding: "6px 12px",
                  minHeight: 28,
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  background: active ? "#22382C" : "#FFFFFF",
                  color: active ? "#F4EFE4" : "#2A2722",
                  border: `1px solid ${active ? "#22382C" : "#E4DCCD"}`,
                  borderRadius: 999,
                  cursor: "pointer",
                  transition: "background .15s, border-color .15s, color .15s",
                }}
                onMouseEnter={(e) => {
                  if (!active) {
                    e.currentTarget.style.borderColor = "#22382C";
                    e.currentTarget.style.background = "#FAF6EC";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    e.currentTarget.style.borderColor = "#E4DCCD";
                    e.currentTarget.style.background = "#FFFFFF";
                  }
                }}
              >
                {c.label}
              </button>
            );
          })}
        </div>

        {/* Secondary filters */}
        <div
          className="audit-secondary-filters"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0,1fr))",
            gap: 10,
            marginBottom: 14,
          }}
        >
          <FilterField label="Actor user_id">
            <input
              value={filter.actor_user_id}
              onChange={(e) => setFilter((f) => ({ ...f, actor_user_id: e.target.value }))}
              placeholder="usr_..."
              data-testid="audit-filter-actor"
              className="al-input"
            />
          </FilterField>
          <FilterField label="Target type">
            <input
              value={filter.target_type}
              onChange={(e) => setFilter((f) => ({ ...f, target_type: e.target.value }))}
              placeholder="project, user, vendor..."
              data-testid="audit-filter-target-type"
              className="al-input"
            />
          </FilterField>
          <FilterField label="From">
            <input
              type="date"
              value={filter.from ? filter.from.slice(0, 10) : ""}
              onChange={(e) =>
                setFilter((f) => ({
                  ...f,
                  from: e.target.value ? `${e.target.value}T00:00:00+00:00` : "",
                }))
              }
              data-testid="audit-filter-from"
              className="al-input"
            />
          </FilterField>
          <FilterField label="To">
            <input
              type="date"
              value={filter.to ? filter.to.slice(0, 10) : ""}
              onChange={(e) =>
                setFilter((f) => ({
                  ...f,
                  to: e.target.value ? `${e.target.value}T23:59:59+00:00` : "",
                }))
              }
              data-testid="audit-filter-to"
              className="al-input"
            />
          </FilterField>
        </div>

        {/* Column header */}
        <div
          className="audit-col-header"
          style={{
            display: "grid",
            gridTemplateColumns: ROW_GRID,
            gap: 12,
            padding: "0 14px",
            marginBottom: 4,
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "#A39A8B",
          }}
        >
          <span>When</span>
          <span>Actor</span>
          <span>Action</span>
          <span>Target</span>
          <span>Detail</span>
          <span>IP</span>
        </div>

        {/* Pill stack */}
        <div data-testid="audit-log-table">
          {loading && rows.length === 0 ? (
            <div
              data-testid="audit-log-loading"
              style={{
                padding: "48px 0",
                textAlign: "center",
                fontSize: 13,
                fontStyle: "italic",
                color: "#76705F",
              }}
            >
              Loading…
            </div>
          ) : !loading && rows.length === 0 ? (
            <div
              data-testid="audit-log-empty"
              style={{
                padding: "48px 0",
                textAlign: "center",
                fontSize: 13,
                fontStyle: "italic",
                color: "#B0A891",
                background: "#FAF6EC",
                border: "1px dashed #E4DCCD",
                borderRadius: 10,
              }}
            >
              No audit events match your filters.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {rows.map((row) => (
                <div
                  key={row.id}
                  data-testid={`audit-row-${row.id}`}
                  style={{
                    display: "grid",
                    gridTemplateColumns: ROW_GRID,
                    gap: 12,
                    alignItems: "center",
                    background: "#FFFFFF",
                    border: "1px solid #EDE5D8",
                    borderRadius: 8,
                    padding: "6px 14px",
                    boxShadow: "0 2px 6px rgba(60,46,20,.05)",
                    transition: "box-shadow .15s ease, transform .15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = "0 5px 14px rgba(60,46,20,.10)";
                    e.currentTarget.style.transform = "translateY(-1px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = "0 2px 6px rgba(60,46,20,.05)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      fontSize: 11,
                      color: "#76705F",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Clock size={11} color="#A39A8B" />
                    <span title={row.created_at}>{timeAgo(row.created_at)}</span>
                  </div>
                  <ActorCell row={row} />
                  <ActionBadge action={row.action} />
                  <TargetCell row={row} />
                  <MetadataPreview row={row} />
                  <IpCell row={row} />
                </div>
              ))}
            </div>
          )}

          {hasMore && (
            <div style={{ textAlign: "center", marginTop: 14 }}>
              <button
                onClick={() => fetchPage(true, cursor)}
                disabled={loading}
                data-testid="audit-load-more-btn"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "6px 14px",
                  minHeight: 28,
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  background: loading ? "#9AAEA3" : "#22382C",
                  color: "#F4EFE4",
                  border: `1px solid ${loading ? "#9AAEA3" : "#22382C"}`,
                  borderRadius: 7,
                  cursor: loading ? "not-allowed" : "pointer",
                  transition: "background .15s",
                }}
                onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = "#2C4A3B"; }}
                onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = "#22382C"; }}
              >
                Load more <ChevronRight size={12} />
              </button>
            </div>
          )}
        </div>

        {/* Append-only footer note */}
        <div
          style={{
            marginTop: 22,
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            fontSize: 11,
            color: "#76705F",
            lineHeight: 1.5,
            maxWidth: 820,
          }}
        >
          <ShieldAlert size={14} color="#A39A8B" style={{ marginTop: 2, flexShrink: 0 }} />
          <span>
            The audit log is append-only by design. Sensitive identifiers
            (emails, usernames) are hashed with a per-studio salt; IPs wipe
            after 180 days. Records outlive the things they reference —
            a &quot;(deleted)&quot; target means the record was purged from Trash,
            but the audit trail of who touched it survives.
          </span>
        </div>
      </div>

      <style>{`
        .al-input{
          width:100%; height:30px; padding:0 9px;
          background:#FFFFFF; border:1px solid #E4DCCD; border-radius:6px;
          font-size:11px; color:#2A2722; outline:none;
          font-family:var(--ds-body-font);
          transition:border-color .15s;
        }
        .al-input:focus{border-color:#22382C;}
        .al-input::placeholder{color:#B0A891;}
      `}</style>
    </Layout>
  );
}

function FilterField({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <div
        style={{
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "#A39A8B",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {children}
    </label>
  );
}
